import React from "react";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="pp-container">
      <div className="pp-content">
        <h1 className="pp-title">Privacy Policy</h1>
        <p className="pp-last-modified">Last modified: October 2026</p>

        <div className="pp-app-banner">
          <h3>Applicability & Applications Covered</h3>
          <p>
            This Privacy Policy governs the collection, use, maintenance, and disclosure of
            information collected from users of our website and mobile applications operated by <strong>True Renewables Private Limited</strong>:
          </p>
          <div className="pp-app-grid">
            <div className="pp-app-card">
              <h4>Organization & Services</h4>
              <p><strong>Organization / Legal Entity:</strong> True Renewables Private Limited </p>
              <p><strong>Official Website:</strong> <a href="https://truerenewables.in" target="_blank" rel="noopener noreferrer">https://truerenewables.in</a></p>
            </div>
            <div className="pp-app-card">
              <h4>Covered Mobile Applications</h4>
              <p><strong>1. True Survey</strong></p>
              <p>Package Name (Android): <code>com.turesurvey.in.true_survey</code></p>
              <p style={{ marginTop: "8px" }}><strong>2. True360</strong></p>
              <p>Package Name (Android): <code>com.trueTrackEpc.True360</code></p>
            </div>
          </div>
        </div>

        <p className="pp-intro">
          At <strong>True Renewables Private Limited</strong>, we respect your privacy and are committed to protecting your
          personal data across our mobile applications (<strong>True Survey</strong>, <strong>True360</strong>) and our website (<strong>https://truerenewables.in</strong>).
          This Privacy Policy explains what information we collect, how it is used, stored, and protected, and the choices you have concerning your data.
        </p>

        <div className="pp-section">
          <h2>1. Information & Permissions We Collect</h2>
          <p>
            Depending on how you use <strong>True Survey</strong>, <strong>True360</strong>, and our website, we may collect the following information:
          </p>

          <h3>A. Personal & Account Information</h3>
          <ul>
            <li>Name</li>
            <li>Email address</li>
            <li>Mobile phone number</li>
            <li>Organization or company information</li>
            <li>Login and authentication information</li>
            <li>Project and user account information</li>
          </ul>

          <h3>B. Location</h3>
          <p>
            <strong>True Survey and True360</strong> may request precise or approximate device location when required for project surveys, EPC project activities, site verification, attendance, check-in, mapping, or other location-based features.
          </p>
          <p>
            Location access is requested through the device's permission system and is used only for relevant application functionality.
          </p>

          <h3>C. Camera</h3>
          <p>
            <strong>True Survey and True360</strong> may request camera access to capture photographs of solar sites, rooftops, electrical panels, utility meters, installations, equipment, and other project-related information.
          </p>
          <p>
            Camera access is used only when the user chooses to use camera-based functionality.
          </p>

          <h3>D. Photos and Images</h3>
          <p>
            <strong>True Survey and True360</strong> may allow users to select and upload images from their device for project surveys, inspections, documentation, reports, and other project-related activities.
          </p>
          <p>
            The applications access images selected by the user for these purposes and do not require unrestricted access to the user's entire photo library.
          </p>

          <h3>E. Device and Diagnostic Information</h3>
          <p>We may collect limited technical information, such as:</p>
          <ul>
            <li>Device model</li>
            <li>Operating system and version</li>
            <li>Application version</li>
            <li>IP address</li>
            <li>Network information</li>
            <li>Crash and diagnostic information</li>
            <li>Performance information</li>
          </ul>
          <p>
            This information is used to maintain application security, diagnose technical problems, and improve application reliability.
          </p>
        </div>

        <div className="pp-section">
          <h2>2. How We Use Your Data</h2>
          <p>We use the collected information for specific, legitimate operational purposes:</p>
          <ul>
            <li>To conduct solar rooftop feasibility analyses and engineering surveys (True Survey).</li>
            <li>To manage EPC project workflows, milestone tracking, and site commissioning (True360).</li>
            <li>To verify field survey locations and site audit entries.</li>
            <li>To generate solar project proposals, layout analysis, and technical reports.</li>
            <li>To provide technical support, account management, and customer assistance.</li>
            <li>To monitor app performance, fix crashes, and improve user experience.</li>
          </ul>
          <p><strong>We do not sell, rent, or lease your personal information or project data to third parties for marketing purposes.</strong></p>
        </div>

        <div className="pp-section">
          <h2>3. Cookies & Tracking Technologies (Website Users)</h2>
          <p>
            When you visit our website (<strong>https://truerenewables.in</strong>), we use cookies and similar tracking technologies to enhance your browsing experience. Cookies help us understand how visitors interact with our site, remember user preferences, and improve usability.
          </p>
          <p>
            You can choose to disable or manage cookies anytime through your web browser settings. Please note that disabling cookies may affect certain interactive features of our website.
          </p>
        </div>

        <div className="pp-section">
          <h2>4. Data Storage & Security</h2>
          <p>
            We implement administrative and technical safeguards to protect your personal information:
          </p>
          <ul>
            <li>Data transmissions between your device or browser and our servers are encrypted using Secure Socket Layer / Transport Layer Security (HTTPS/TLS).</li>
            <li>Access to personal, client, and project data is restricted strictly to authorized personnel and team members who require it for operational purposes.</li>
          </ul>
        </div>

        <div className="pp-section">
          <h2>5. Sharing Information & Third-Party Services</h2>
          <p>We may share necessary data only under strict confidentiality agreements with:</p>
          <ul>
            <li><strong>Authorized Service Partners & Vendors:</strong> Partners who assist in solar feasibility, engineering design, and project delivery.</li>
            <li><strong>Infrastructure & Hosting Providers:</strong> Cloud hosting and database services that maintain our apps and website.</li>
            <li><strong>Mapping & Geocoding Services:</strong> APIs that support survey coordinate verification and mapping features.</li>
            <li><strong>Government or Regulatory Agencies:</strong> When strictly required by applicable law, court order, or statutory regulations.</li>
          </ul>
          <p>All third parties handling data on our behalf are bound by non-disclosure and strict confidentiality obligations.</p>
        </div>

        <div className="pp-section">
          <h2>6. Data Retention & Account / Data Deletion Policy</h2>
          <p>
            We retain personal information and project survey data only as long as necessary to fulfill the purposes for which it was collected, or as required by applicable legal, operational, and regulatory standards.
          </p>

          <div className="pp-callout">
            <p><strong>How to Request Account & Data Deletion:</strong></p>
            <p>
              Users of <strong>True Survey</strong>, <strong>True360</strong>, or our website have the right to request the permanent deletion of their account and associated personal data at any time.
            </p>
            <p>
              To request deletion, please email us at <a href="mailto:Care@truerenewables.in?subject=Account%20and%20Data%20Deletion%20Request">Care@truerenewables.in</a> with the subject line: <em>"Account / Data Deletion Request - [Your Registered Email or App Name]"</em>.
            </p>
            <p>
              Upon receiving your request, we will verify your identity and delete your account and associated personal records within a reasonable period, subject to statutory legal and operational retention requirements.
            </p>
          </div>
        </div>

        <div className="pp-section">
          <h2>7. Your Rights & Choices</h2>
          <p>You have full control over your personal data. You may:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong>Rectification:</strong> Ask for corrections or updates to inaccurate details.</li>
            <li><strong>Revoke Permissions:</strong> Enable or disable device permissions (Camera, Location) at any time through your mobile device system settings.</li>
            <li><strong>Data Deletion:</strong> Request complete erasure of your account and personal data.</li>
            <li><strong>Communications:</strong> Unsubscribe from non-essential promotional communications at any time.</li>
          </ul>
          <p>
            To exercise any of these rights, email us at <a href="mailto:Care@truerenewables.in">Care@truerenewables.in</a>.
          </p>
        </div>

        <div className="pp-section">
          <h2>8. External Links & Third-Party Websites</h2>
          <p>
            Our website and apps may include links to external websites and resources for your convenience. Once you leave our domain, we are not responsible for how those third-party websites handle your privacy and data. We encourage you to review their individual privacy policies.
          </p>
        </div>

        <div className="pp-section">
          <h2>9. Policy For Children</h2>
          <p>
            Our applications (<strong>True Survey</strong>, <strong>True360</strong>) and services are intended for professional, commercial, and adult users (aged 18 and above). We do not knowingly collect or solicit personal information from minors. If you believe a child has shared data with us, please contact us at <a href="mailto:Care@truerenewables.in">Care@truerenewables.in</a> for immediate deletion.
          </p>
        </div>

        <div className="pp-section">
          <h2>10. Changes To This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our services, mobile apps, or regulatory requirements. Changes will be posted on this page, and the "Last modified" date above will be updated accordingly.
          </p>
        </div>

        <div className="pp-section">
          <h2>11. Contact Us</h2>
          <p>For any privacy-related questions, data requests, or inquiries, contact us at:</p>
          <ul>
            <li><strong>Organization / Legal Entity:</strong> True Renewables Private Limited</li>
            <li><strong>Email:</strong> <a href="mailto:Care@truerenewables.in">Care@truerenewables.in</a></li>
            <li><strong>Website:</strong> <a href="https://truerenewables.in" target="_blank" rel="noopener noreferrer">https://truerenewables.in</a></li>
            <li>
              <strong>Address:</strong> Plot No 1/C, Sy No 83/1, Raidurgam, Knowledge City Road, Panmaktha, Hyderabad – 500032, Telangana, India
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;


